import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { AppButton } from '../../button/app-button';
import {
  ListSkeleton,
  LoadError,
} from '../dashboard/dashboard-shared-components/load-state';
import { Staleable, StateSwitch } from '../../../motion';
import { TenantOverview } from '../../../api/tenant-client';
import { formatDate } from '../../../function-utils/locale';
import { countOf } from '../../../function-utils/plural';
import { readFailure, usePortalOverview } from './portal-common';
import { PushTipCard, usePushTip } from './portal-push-tip';
import {
  Card,
  EmptyState,
  Fact,
  FactLabel,
  FactMeta,
  FactValue,
  Greeting,
  Headline,
  HeadlineNote,
  HeadlineText,
  HomeGrid,
  HomeGroup,
  Section,
  SectionHead,
  SectionHint,
  SectionTitle,
  Sub,
} from './portal-kit.styled';

/**
 * HOME — the answer to "is there anything for me", then where you live.
 *
 * The headline states the parcel answer in a sentence rather than as a number
 * in a tile: "You have 2 parcels waiting" is read; a large "2" over the word
 * "Parcels" has to be decoded. Its counts are the server's, on the building's
 * desk day, and — since migration 114 — never count the household's own
 * Resident Entry rows as somebody here to see them.
 *
 * EVERY HOME. A resident with two open occupancies used to be shown one of them
 * while their parcel and visitor lists covered both; each home is its own
 * Building + Unit pair now, the primary one first.
 */

interface HomeView {
  key: string;
  building: string;
  address: string;
  unit: string;
  movedIn: string | null;
}

const joinAddress = (...parts: (string | null | undefined)[]): string =>
  parts.filter(Boolean).join(', ');

/* The homes to show, from `homes` when the server sends it, else from the two
   keys the portal has always read — so an older server still renders a home
   rather than "no building". */
const homeViews = (overview: TenantOverview | null): HomeView[] => {
  if (!overview) return [];
  if (overview.homes && overview.homes.length) {
    return overview.homes.map(home => ({
      key: home.occupancy_id,
      building: home.building_name,
      address: joinAddress(home.building_address, home.building_country),
      unit: home.unit_number || home.unit_name || '',
      movedIn: home.moved_in,
    }));
  }
  if (overview.building) {
    return [
      {
        key: 'home',
        building: overview.building.property_name,
        address: joinAddress(overview.building.address, overview.building.country),
        unit: overview.unit?.unit_number || '',
        movedIn: overview.unit?.moved_in || null,
      },
    ];
  }

  return [];
};

/* A move-in is a calendar DATE ('2024-03-12'). new Date() reads that as UTC
   midnight, and the old page then printed it in the phone's zone — a day early
   anywhere west of Greenwich. Read and printed in UTC, it stays the date it is. */
const movedInLabel = (value: string | null): string => {
  if (!value) return '';
  const at = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value
  );
  if (Number.isNaN(at.getTime())) return '';

  return `Resident since ${formatDate(at, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })}`;
};

/* The headline is a claim about the shelf, and may only be made when the shelf
   was actually counted. */
const headlineFor = (waiting: number | undefined, hasHome: boolean): string => {
  if (typeof waiting !== 'number') {
    return hasHome
      ? 'We could not check the front desk just now.'
      : 'Nothing is waiting for you at the desk.';
  }
  if (waiting === 1) return 'You have a parcel waiting at the front desk.';
  if (waiting > 1) return `You have ${waiting} parcels waiting at the front desk.`;

  return 'Nothing is waiting for you at the desk.';
};

export const PortalHome: React.FC = () => {
  useDocumentTitle('Your home');
  const navigate = useNavigate();
  const {
    data: overview,
    status,
    failed,
    errorStatus,
    refreshing,
    reload,
  } = usePortalOverview();
  const pushTip = usePushTip();

  const homes = homeViews(overview);
  const waiting = overview?.counts?.parcelsWaiting;
  const inside = overview?.counts?.myVisitorsInside || 0;
  const firstName = overview?.profile?.first_name || 'Resident';

  return (
    <>
      {/* THE ONE h1 ON THIS TAB, present in every state — a failed or loading
          overview still leaves the page with a heading to land on. */}
      <Greeting>{overview ? `Welcome, ${firstName}` : 'Welcome'}</Greeting>
      <Sub>Your parcels, your visitors and your home.</Sub>

      {/* First visit: placeholder rows the shape of what is coming. A refresh
          (coming back to the tab) keeps the page and dims it. */}
      <StateSwitch state={status} minHeight={320}>
        {status === 'loading' ? (
          <ListSkeleton rows={3} label="your home" />
        ) : status === 'error' ? (
          <LoadError
            message={readFailure(errorStatus, 'We could not load your home just now.')}
            onRetry={reload}
          />
        ) : (
          <Staleable stale={refreshing}>
            {/* WHAT COULD NOT BE READ, above the claim it undermines. A
                resident reading "Nothing is waiting for you" needs to know when
                that sentence is from an earlier read. */}
            {failed ? (
              <LoadError
                message={readFailure(
                  errorStatus,
                  'We could not refresh your home just now. What is shown may be out of date.'
                )}
                onRetry={reload}
              />
            ) : null}

            <Headline $quiet={!waiting}>
              <HeadlineText>{headlineFor(waiting, homes.length > 0)}</HeadlineText>
              {inside ? (
                <HeadlineNote>
                  {inside === 1
                    ? 'Somebody is signed in to see you right now.'
                    : `${inside} people are signed in to see you right now.`}
                </HeadlineNote>
              ) : null}
              {/* THE MOST VALUABLE THING A RESIDENT CAN DO, OFFERED WHERE THEY
                  ARE: being told the moment a parcel arrives. While the push
                  tip below is on offer it asks the same question, so this link
                  steps aside; once the tip is gone (put away, push on, or a
                  browser with no push, where email is the way) it is back. */}
              {pushTip.settled && !pushTip.offered ? (
                <HeadlineNote>
                  <AppButton
                    variant="ghost"
                    size="compact"
                    onClick={() => navigate('/account/notifications')}
                  >
                    Get told when a parcel arrives
                  </AppButton>
                </HeadlineNote>
              ) : null}
            </Headline>

            <PushTipCard tip={pushTip} />

            <Section>
              <SectionHead>
                <SectionTitle>Your home</SectionTitle>
                {homes.length > 1 ? (
                  <SectionHint>{countOf(homes.length, 'home')}</SectionHint>
                ) : null}
              </SectionHead>
              <Card>
                {homes.length ? (
                  homes.map(home => {
                    const since = movedInLabel(home.movedIn);
                    const unit = home.unit || 'Not assigned';

                    return (
                      <HomeGroup
                        key={home.key}
                        role={homes.length > 1 ? 'group' : undefined}
                        aria-label={
                          homes.length > 1
                            ? `${home.building}, unit ${unit}`
                            : undefined
                        }
                      >
                        <HomeGrid>
                          <Fact>
                            <FactLabel>Building</FactLabel>
                            <FactValue>{home.building}</FactValue>
                            {home.address ? (
                              <FactMeta>{home.address}</FactMeta>
                            ) : null}
                          </Fact>
                          <Fact>
                            <FactLabel>Unit</FactLabel>
                            <FactValue>{unit}</FactValue>
                            {since ? <FactMeta>{since}</FactMeta> : null}
                          </Fact>
                        </HomeGrid>
                      </HomeGroup>
                    );
                  })
                ) : (
                  <EmptyState>
                    No building is assigned to your account yet. Please contact
                    your property manager.
                  </EmptyState>
                )}
              </Card>
            </Section>
          </Staleable>
        )}
      </StateSwitch>
    </>
  );
};
